import { ReactNode, useEffect } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import {
  useAdminCreateGatheringSpotType,
  useAdminUpdateGatheringSpotType,
  useGetGatheringSpotTypeById,
} from '@outdoria/api';
import { Button, Icon, IconType, Loader, icons } from '@outdoria/ui';
import { GatheringSpotType } from '@outdoria/types';
import { ActionBar } from '../../containers';
import { Form } from '../../components';
import { useForm } from '../../hooks';

import s from './GatheringSpotTypeUpsertView.module.scss';

/**
 * Create a new gathering spot type.
 */
export const GatheringSpotTypeCreateView = () => {
  const { mutate } = useCreate();

  const defaultData = {
    name: '',
    description: '',
    icon: 'Tree' as IconType,
  };

  return (
    <GatheringSpotTypeUpsertView
      defaultData={defaultData}
      actionLabel="Save new gathering spot type"
      mutate={mutate}
    />
  );
};

/**
 * Edit the selected gathering sopt type.
 */

export const GatheringSpotTypeEditView = () => {
  const { gathering_spot_type_id = 'UNKNOWN' } =
    useParams<GatheringSpotType['gathering_spot_type_id']>();
  const {
    data: gatheringSpotType,
    isLoading,
    isError,
  } = useGetGatheringSpotTypeById(gathering_spot_type_id);

  const { mutate } = useUpdate(gathering_spot_type_id);

  if (isLoading) return <Loader />;

  if (isError) return <div>I broke :c</div>;

  return (
    <GatheringSpotTypeUpsertView
      defaultData={gatheringSpotType}
      actionLabel="Save updated gathering spot type"
      mutate={mutate}
    />
  );
};

/**
 * Reused part.
 */
type GatheringSpotTypeUpsertViewProps = {
  defaultData: GatheringSpotTypeNoId;
  actionLabel: string;
  mutate: (form: GatheringSpotTypeNoId) => void;
};
const GatheringSpotTypeUpsertView = ({
  defaultData,
  actionLabel,
  mutate,
}: GatheringSpotTypeUpsertViewProps) => {
  const { register, form, onReset } =
    useForm<GatheringSpotTypeNoId>(defaultData);

  const onGatheringSpotTypeCreate = () => {
    mutate(form);
  };

  const getIcons = () =>
    Object.keys(icons).map(icon => ({
      label: <Icon icon={icon as IconType} />,
      value: icon,
    })) as { value: IconType; label: ReactNode }[];

  return (
    <div className={s.view}>
      <ActionBar
        buttons={
          <>
            <Button label="Reset form" onClick={onReset} />
            <Button
              label={actionLabel}
              variant={Button.Variant.ACTION}
              onClick={onGatheringSpotTypeCreate}
            />
          </>
        }
      />

      <div className={s.view__content}>
        <div className={s.view__row}>
          <Form title="Gathering spot type properties" icon="Pin" pretty>
            <Form.Input
              label="Name"
              {...register('name', { required: true })}
            />

            <Form.Input
              label="Description"
              {...register('description', { required: true })}
            />

            <Form.RadioGroup
              label="Icon"
              data={getIcons()}
              {...register('icon', { required: true })}
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

const useCreate = () => {
  const { mutate, error, isSuccess } = useAdminCreateGatheringSpotType();

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Gathering spot type created :3`);
    }
  }, [isSuccess]);

  return { mutate };
};

const useUpdate = (gathering_spot_type_id: string) => {
  const { mutate, error, isSuccess } = useAdminUpdateGatheringSpotType(
    gathering_spot_type_id,
  );

  useEffect(() => {
    if (error) toast.error(error?.message ?? 'Something went wrong :c');
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Yay! Gathering spot type updated :3`);
    }
  }, [isSuccess]);

  return { mutate };
};

type GatheringSpotTypeNoId = Omit<GatheringSpotType, 'gathering_spot_type_id'>;
